import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import dayjs, { Dayjs } from '@wox/dayjs'
import { KBFlex, KBSpin, KBText, KBDatePicker } from '@/components/atoms'
import { KBEmpty } from '@/components/organisms'
import SlotCard from './SlotCard'
import {
  useResourceAvailableSlots,
  type EffectiveSlot,
} from '@/hooks/useBookingSlots'

export interface SlotSelection {
  templateId: number
  slotDate: string
  startAt: string
  endAt: string
  name: string
}

export interface KBSlotSelectorProps {
  resourceId: number
  timezone?: string
  value?: SlotSelection
  onChange?: (selection: SlotSelection | undefined) => void
  disabled?: boolean
}

const KBSlotSelector: React.FC<KBSlotSelectorProps> = ({
  resourceId,
  timezone = Intl.DateTimeFormat().resolvedOptions().timeZone,
  value,
  onChange,
  disabled = false,
}) => {
  const { t } = useTranslation()
  // browseDateStr drives the displayed date and slot query.
  // Initialized from value.slotDate if present, otherwise today.
  const [browseDateStr, setBrowseDateStr] = useState<string>(
    value?.slotDate || dayjs().tz(timezone).format('YYYY-MM-DD')
  )

  // Keep browseDateStr in sync when value.slotDate changes externally (e.g. edit mode fill)
  useEffect(() => {
    if (value?.slotDate) {
      setBrowseDateStr(value.slotDate)
    }
  }, [value?.slotDate])

  const dateStr = browseDateStr
  // Must use dayjs.tz() to construct date in target timezone.
  // Plain dayjs(dateStr) gets auto-converted by customDayjs wrapper via .tz(storedTz, false),
  // which shifts the date when browser tz differs from location tz.
  const displayDate = useMemo(
    () => dayjs.tz(dateStr, timezone),
    [dateStr, timezone]
  )
  const dates = useMemo(() => [dateStr], [dateStr])

  const { data: slotsData, isLoading } = useResourceAvailableSlots(
    resourceId,
    dates,
    timezone
  )

  const slots = useMemo(
    () => (slotsData?.[dateStr] || []).filter((s) => !s.is_carryover),
    [slotsData, dateStr]
  )

  const handleDateChange = (date: Dayjs | null) => {
    if (date) {
      setBrowseDateStr(date.format('YYYY-MM-DD'))
      // selection will be updated by the useEffect below after new slots load
    }
  }

  const handleSlotSelect = (slot: EffectiveSlot) => {
    if (disabled || !slot.is_available || slot.remaining_capacity === 0) return

    const selection: SlotSelection = {
      templateId: slot.template_id,
      slotDate: dateStr,
      startAt: slot.start_at,
      endAt: slot.end_at,
      name: slot.name,
    }

    onChange?.(selection)
  }

  // Auto-select a slot after slots load (on date change or initial load).
  // Prefer the same template_id as the previous selection; fall back to first available.
  useEffect(() => {
    if (isLoading) return
    if (slots.length === 0) {
      if (value) onChange?.(undefined)
      return
    }

    const prevTemplateId = value?.templateId
    const sameSlot = prevTemplateId
      ? slots.find(
          (s) =>
            s.template_id === prevTemplateId &&
            s.is_available &&
            s.remaining_capacity > 0
        )
      : undefined
    const targetSlot =
      sameSlot || slots.find((s) => s.is_available && s.remaining_capacity > 0)

    if (targetSlot) {
      handleSlotSelect(targetSlot)
    } else {
      onChange?.(undefined)
    }
  }, [slots, dateStr]) // eslint-disable-line

  const disabledDate = (date: Dayjs) =>
    date.isBefore(dayjs().tz(timezone), 'day')

  return (
    <KBFlex vertical gap={16} className="tw-w-full">
      {/* Date Picker */}
      <KBFlex gap={8} align="center">
        <KBText className="tw-text-gray-600">
          {t('booking.slot.selectDate')}:
        </KBText>
        <KBDatePicker
          value={displayDate}
          onChange={handleDateChange}
          disabledDate={disabledDate}
          disabled={disabled}
          allowClear={false}
        />
      </KBFlex>

      {/* Slots Grid */}
      {isLoading ? (
        <KBFlex justify="center" className="tw-py-8">
          <KBSpin />
        </KBFlex>
      ) : slots.length === 0 ? (
        <div className="tw-py-8">
          <KBEmpty title={t('booking.slot.noSlotsAvailable')} />
        </div>
      ) : (
        <div className="tw-grid tw-grid-cols-1 tw-gap-3 sm:tw-grid-cols-2 lg:tw-grid-cols-3">
          {slots.map((slot) => (
            <SlotCard
              key={slot.template_id}
              slot={slot}
              timezone={timezone}
              selected={
                value?.templateId === slot.template_id &&
                value?.slotDate === dateStr
              }
              disabled={disabled}
              onClick={() => handleSlotSelect(slot)}
            />
          ))}
        </div>
      )}
    </KBFlex>
  )
}

export default KBSlotSelector
